<template>
  <div class="settings flex no-wrap q-gutter-16">
    <q-btn
      dense
      flat
      class="currency"
      :label="currency.symbol"
      @click="settingKey = 'currency'"
    />
    <q-btn
      v-if="false"
      dense
      flat
      no-caps
      class="distance-unit"
      :label="$t(`label.distance.${values.distance_unit}`)"
      @click="settingKey = 'distance_unit'"
    />
    <q-btn
      dense
      flat
      class="locale"
      @click="settingKey = 'locale'"
    >
      <Flag :code="currentLocale" />
    </q-btn>
    <q-btn-dropdown
      v-if="account && !account.require_contacts"
      dense
      flat
    >
      <template #label>
        <div
          v-if="isVipPartner"
          class="icon-vip"
        />
        <q-icon
          v-else
          size="xs"
          name="person"
        />
        <Badge
          v-if="accountLinks.some((el) => el.hasBadge)"
          size="12px"
          :left="isVipPartner ? '80%' : '50%'"
        />
      </template>
      <q-list>
        <q-item
          v-for="{ key, icon, link, hasBadge } in accountLinks"
          :key="key"
          v-close-popup
          clickable
          tag="a"
          :href="`/${currentLocale}/${link || key}`"
        >
          <q-item-section
            class="relative-position"
            avatar
          >
            <q-icon :name="icon" />
            <Badge
              v-if="hasBadge"
              left="25%"
            />
          </q-item-section>
          <q-item-section>
            {{ $t(`cabinet_links.${key}`) }}
          </q-item-section>
        </q-item>
        <q-item
          v-close-popup
          clickable
          @click="logout(redirectAfterLogout)"
        >
          <q-item-section avatar>
            <q-icon name="logout" />
          </q-item-section>
          <q-item-section>
            {{ $t('button.log_out') }} {{ account.email }}
          </q-item-section>
        </q-item>
        <slot name="after" />
      </q-list>
    </q-btn-dropdown>
    <div
      v-else
      @click="bus.emit('show-auth-modal', { openedFrom: 'header_settings' })"
    >
      <q-btn
        class="lt-md"
        dense
        flat
        icon="person"
        size="sm"
      />
      <q-btn
        class="gt-sm"
        flat
        no-caps
        :label="domain.is.aeroflot ? $t('domains.aeroflot.login_button') : $t('label.auth.login_or_signup')"
      />
    </div>
    <SettingsSheet
      v-if="currentSetting"
      v-bind="currentSetting"
      ref="settingsSheetRef"
      :value="values[settingKey]"
      :autoclose="false"
      @change="onChangeSetting"
      @hide="settingKey = null"
    />
  </div>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { storeToRefs } from 'pinia';

import Badge from '~components/Badge';
import Flag from '~components/Flag';
import SettingsSheet from '~components/settings/SettingsSheet';

import useAccountMethods from '~composables/useAccountMethods';
import useLocaleUrl from '~composables/useLocaleUrl';
import useSettings from '~composables/useSettings';
import { useAccountStore } from '~stores/account';

const accountStore = useAccountStore();

const { logout } = useAccountMethods();
const { currentLocale } = useLocaleUrl();
const {
  settingKey,
  values,
  currentSetting,
  currency,
  setSetting,
} = useSettings();

defineProps({
  redirectAfterLogout: {
    type: String,
    default: null,
  },
});

const bus = inject('bus');
const domain = inject('domain');

const settingsSheetRef = ref(null);

const {
  account,
  isVipPartner,
  isWrongContacts,
  isFamilyUiType,
} = storeToRefs(accountStore);

const accountLinks = computed(() => {
  const links = [
    {
      key: 'account',
      icon: 'settings',
      hasBadge: isWrongContacts.value,
    },
  ];

  if (account.value.carrier) {
    links.unshift({
      key: 'carrier',
      icon: 'directions_car',
    });
  }

  if (isFamilyUiType.value) {
    links.unshift({
      key: 'passenger',
      link: 'client',
      icon: 'passenger',
    });
  } else {
    links.unshift({
      key: 'business',
      link: 'client',
      icon: 'work',
    });
  }

  return links;
});

async function onChangeSetting(value) {
  await setSetting(value, true);
  settingsSheetRef.value.$refs.dialogRef.hide();
}
</script>

<style lang="scss" scoped>
.settings {
  :deep(.q-btn-dropdown--simple .q-btn-dropdown__arrow) {
    display: none;
  }

  .q-btn {
    padding: 8px;

    @media screen and (max-width: $breakpoint-sm-max) {
      padding: 2px;
    }
  }

  .badge {
    @media screen and (min-width: $breakpoint-md-min) {
      top: 6px !important;
    }
  }

  .icon-vip {
    content: url('/common/icons/partner-vip.svg');
  }
}
</style>
